// Commentaires et photos d'un inventaire (décisions 6, 14, 31, 32 et 48) : deux listes, chaque élément
// rattaché à un thème (themeId) ou à l'inventaire entier (themeId null), saisi pendant l'inventaire
// ou ajouté après l'enregistrement (after). Plusieurs commentaires possibles par thème.
//
// Commentaire : { id, themeId, comment, commentUser, commentTime, commentEditTime, commentHistory, commentDeletedTime, after }
// Photo : { id, url, file, description, user, time, themeId, after }

import { plural } from './fieldReference'

export const GENERAL_COMMENT_ID = 'general'

let sequence = 0

function nextId(prefix)
{
	sequence++
	return prefix + '-' + Date.now() + '-' + sequence
}

export function emptyNotes()
{
	return { comments: [], photos: [] }
}

export function isActiveComment(comment)
{
	return !comment.commentDeletedTime && comment.comment.trim() !== ''
}

// Commentaires proposés à la jointure d'une intervention : [{ id, text, user, userId, time }], du plus ancien au
// plus récent. user : le nom de l'auteur quand on le connaît déjà (la personne connectée), sinon vide ;
// le formulaire retrouve le nom des autres avec userId.
export function commentChoices(comments, user)
{
	const mine = user ? ((user.firstname || '') + ' ' + (user.lastname || '')).trim() : ''
	return (comments || [])
		.filter(isActiveComment)
		.sort((a, b) => a.commentTime - b.commentTime)
		.map(comment => ({ id: comment.id, text: comment.comment.trim(), user: user && comment.commentUser === user._id ? mine : '', userId: comment.commentUser || null, time: comment.commentTime }))
}

function themeOrder(themes)
{
	const order = {}
	themes.forEach((theme, index) => {
		order[theme.id] = index
	})
	return order
}

function themeNameOf(themes, themeId)
{
	const theme = themes.find(item => item.id === themeId)
	return theme ? theme.name : null
}

// Dans l'ordre du modèle, puis l'inventaire entier, puis par date
function sortByTheme(themes, list, timeOf)
{
	const order = themeOrder(themes)
	const rank = (item) => (item.themeId === null || order[item.themeId] === undefined ? themes.length : order[item.themeId])
	return list.slice().sort((a, b) => rank(a) - rank(b) || timeOf(a) - timeOf(b))
}

// Éléments affichés : { comment, themeName } et { photo, themeName }
export function duringCommentItems(themes, notes)
{
	const list = notes.comments.filter(comment => !comment.after && isActiveComment(comment))
	return sortByTheme(themes, list, comment => comment.commentTime).map(comment => ({ comment, themeName: themeNameOf(themes, comment.themeId) }))
}

export function afterCommentItems(themes, notes)
{
	return notes.comments
		.filter(comment => comment.after && isActiveComment(comment))
		.sort((a, b) => a.commentTime - b.commentTime)
		.map(comment => ({ comment, themeName: themeNameOf(themes, comment.themeId) }))
}

export function themeCommentItems(notes, themeId)
{
	return notes.comments
		.filter(comment => comment.themeId === themeId && isActiveComment(comment))
		.sort((a, b) => a.commentTime - b.commentTime)
		.map(comment => ({ comment, themeName: null }))
}

export function duringPhotoItems(themes, notes)
{
	const list = notes.photos.filter(photo => !photo.after)
	return sortByTheme(themes, list, photo => photo.time).map(photo => ({ photo, themeName: themeNameOf(themes, photo.themeId) }))
}

export function afterPhotoItems(notes)
{
	return notes.photos.filter(photo => photo.after).map(photo => ({ photo, themeName: null }))
}

export function themePhotos(notes, themeId)
{
	return notes.photos.filter(photo => photo.themeId === themeId)
}

export function withPhoto(notes, photo)
{
	return { ...notes, photos: notes.photos.map(item => (item.id === photo.id ? photo : item)) }
}

// Lignes « photo » et « commentaire » d'un thème : « Ajouter… » tant qu'il n'y a rien,
// puis « Voir… » (retour d'Alexandra du 24/10/2026)
export function photoAction(count)
{
	if (count === 0)
		return { title: 'Ajouter une photo', subtitle: 'Facultatif, pour ce thème' }
	return { title: count === 1 ? 'Voir la photo' : 'Voir les ' + count + ' photos', subtitle: 'Ou en ajouter une autre' }
}

export function commentAction(count)
{
	if (count === 0)
		return { title: 'Ajouter un commentaire', subtitle: 'Facultatif, pour ce thème' }
	return { title: count === 1 ? 'Voir le commentaire' : 'Voir les ' + count + ' commentaires', subtitle: 'Ou en ajouter un autre' }
}

export function newComment({ themeId, text, user, now, after })
{
	return { id: nextId('comment'), themeId: themeId === undefined ? null : themeId, comment: text, commentUser: user ? user._id : null, commentTime: now, after: !!after }
}

export function withComment(notes, comment)
{
	const exists = notes.comments.some(item => item.id === comment.id)
	return { ...notes, comments: exists ? notes.comments.map(item => (item.id === comment.id ? comment : item)) : notes.comments.concat([comment]) }
}

export function withPhotos(notes, photos, themeId, after)
{
	return { ...notes, photos: notes.photos.concat(photos.map(photo => ({ ...photo, themeId: themeId === undefined ? null : themeId, after: !!after }))) }
}

export function withoutPhotos(notes, ids)
{
	return { ...notes, photos: notes.photos.filter(photo => !ids.includes(photo.id)) }
}

// Emplacement sans modèle (W7f) : un seul commentaire libre, saisi directement dans le champ
export function generalComment(notes)
{
	return notes.comments.find(comment => comment.id === GENERAL_COMMENT_ID) || null
}

export function withGeneralComment(notes, text, user, now)
{
	const current = generalComment(notes)
	const comment = current
		? { ...current, comment: text, commentTime: now }
		: { ...newComment({ themeId: null, text, user, now, after: false }), id: GENERAL_COMMENT_ID }
	return withComment(notes, comment)
}

export function hasNotes(notes)
{
	return notes.photos.length > 0 || notes.comments.some(isActiveComment)
}
